/**
 * Williams API Service
 * API client for TRFer caseload copilot chat endpoints.
 */

import { apiClient } from './api';
import type { ApiResponse } from '@/types/api';
import type { ChatMessageState, ConversationSummary } from '@/types/chat';
import { normalizeRawMessage, type RawMessage } from './chatHelpers';

export const williamsApi = {
  /**
   * POST /api/trfer/williams/session
   * Creates a Williams chat session and returns conversationId.
   */
  createSession: async (conversationId?: string): Promise<string> => {
    const response = await apiClient.post<ApiResponse<{ conversationId: string }>>(
      '/trfer/williams/session',
      conversationId ? { conversationId } : {}
    );
    return response.data.data?.conversationId ?? '';
  },

  /**
   * GET /api/trfer/williams/conversations
   * Returns TRFer's Williams conversation list.
   */
  getConversations: async (): Promise<ConversationSummary[]> => {
    const response = await apiClient.get<
      ApiResponse<{ conversations: ConversationSummary[] }>
    >('/trfer/williams/conversations');
    return response.data.data?.conversations ?? [];
  },

  /**
   * GET /api/trfer/williams/history/:conversationId
   * Returns messages for a Williams conversation.
   */
  getHistory: async (
    conversationId: string,
    limit = 50
  ): Promise<ChatMessageState[]> => {
    const response = await apiClient.get<
      ApiResponse<{ conversation_id: string; messages: RawMessage[] }>
    >(`/trfer/williams/history/${conversationId}`, { params: { limit } });

    const messages = response.data.data?.messages ?? [];
    return messages.map(normalizeRawMessage);
  },

  /**
   * POST /api/trfer/williams/stream/:conversationId/message
   * Send a message (response arrives via SSE).
   */
  sendStreamingMessage: async (
    conversationId: string,
    message: string,
  ): Promise<void> => {
    const response = await apiClient.post(
      `/trfer/williams/stream/${conversationId}/message`,
      { message }
    );
    if (response.data.status !== 'success') {
      throw new Error(
        response.data.error || response.data.message || 'Couldn’t send your message. Please try again.',
      );
    }
  },

  /**
   * POST /api/trfer/williams/attachments
   * Uploads a document for the current Williams thread. The backend extracts
   * text server-side and threads it into the next agent turn, so `extracted`
   * is false (with an honest `note`) whenever extraction couldn't run — e.g.
   * an image, which is stored but not read.
   */
  uploadAttachment: async (
    conversationId: string,
    file: File,
  ): Promise<{ attachment_id: string; filename: string; extracted: boolean; note?: string }> => {
    const form = new FormData();
    // Field name must stay 'file' — it is what the backend's multer
    // upload.single('file') accepts (routes/trfer/williams-attachments.js).
    form.append('file', file);
    form.append('conversation_id', conversationId);
    const response = await apiClient.post<
      ApiResponse<{ attachment_id: string; filename: string; extracted: boolean; note?: string }>
    >('/trfer/williams/attachments', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (!response.data.data) {
      throw new Error(response.data.message || 'Couldn’t attach that file. Please try again.');
    }
    return response.data.data;
  },
};

export default williamsApi;
